import React, { useState, useMemo } from "react";
import { Heading, Loading, DataTable, DateDisplay, Button } from "@aidkitorg/component-library";
import { useContext } from "react";
import { UserInfoContext } from "../../Context";
import { useAsyncEffect } from "../../Hooks/AsyncEffect";
import { usePost } from "../../API";
import { ThreeColumnPage } from "../../Components/ThreeColumnPage";
import { useHistory } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import type { Model as ScoreModel } from "@aidkitorg/aidkit/lib/convoai/schemas/Score";
import type { AIConversationAnalytics } from "@aidkitorg/aidkit/lib/convoai/conversation.schema";
import { formatDuration } from "../../utils/time";
import { useLocalizedStrings } from "../../Localization";

type ConversationRow = AIConversationAnalytics;

export function ConversationReviewList() {
  const [loading, setLoading] = useState<boolean>(false);
  const [conversations, setConversations] = useState<ConversationRow[]>([]);
  const [reviewedConversations, setReviewedConversations] = useState<ConversationRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const userInfo = useContext(UserInfoContext);
  const getConversations = usePost('/coach/conversation/explore');
  const getScores = usePost('/coach/conversation/scores');
  const history = useHistory();
  const L = useLocalizedStrings();

  const [reviewMeta, setReviewMeta] = useState<Record<string, { reviewed_at?: string, scores: Record<string, number> }>>({});

  const scoreTypes = useMemo(() => {
    const set = new Set<string>();
    Object.values(reviewMeta).forEach(meta => {
      Object.keys(meta?.scores || {}).forEach(k => set.add(k));
    });
    return Array.from(set).sort();
  }, [reviewMeta]);

  const buildColumns = (history: ReturnType<typeof useHistory>, variant: 'toReview' | 'reviewed', scoreTypes: string[] = []): ColumnDef<ConversationRow>[] => {
    let cols: ColumnDef<ConversationRow>[] = [
      {
        header: L.ai_coach.dashboard.conversation_id,
        accessorKey: "id",
        size: 120,
        minSize: 80,
        maxSize: 160,
      },
    ];

    if (variant === 'toReview') {
      cols = cols.concat([
        {
          header: L.ai_coach.dashboard.created,
          accessorKey: "created_at",
          cell: ({ row }: { row: { original: ConversationRow } }) => (
            <DateDisplay
              utcValue={row.original.created_at}
              className="text-sm"
              optionOverrides={{ dateStyle: 'long', timeStyle: 'short', timeZoneName: undefined }}
              displayInUTC={false}
            />
          )
        },
        { header: L.ai_coach.dashboard.applicant, accessorKey: "legal_name" },
        {
          header: L.ai_coach.dashboard.duration,
          id: "duration",
          cell: ({ row }: { row: { original: ConversationRow } }) => (
            <span className="text-sm">{formatDuration(row.original.duration, 'seconds')}</span>
          )
        },
        { header: L.ai_coach.dashboard.tokens, accessorKey: "tokens_total" },
        {
          header: L.ai_coach.dashboard.url,
          id: "url",
          cell: ({ row }: { row: { original: ConversationRow } }) => (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => history.push(`/ai/config/review/${row.original.id}`)}
            >
              {L.ai_coach.dashboard.view_conversation}
            </Button>
          )
        },
        {
          header: L.ai_coach.dashboard.actions,
          id: "actions",
          cell: ({ row }: { row: { original: ConversationRow } }) => (
            <Button variant="primary" size="sm" onClick={() => history.push(`/ai/config/review/${row.original.id}`)}>
              Start Review
            </Button>
          )
        }
      ]);
    }

    if (variant === 'reviewed') {
      cols = cols.concat([
        { header: L.ai_coach.dashboard.applicant, accessorKey: "legal_name" },
        {
          header: L.ai_coach.dashboard.reviewed_at,
          id: "reviewed_at",
          cell: ({ row }: { row: { original: ConversationRow } }) => (
            reviewMeta[row.original.id]?.reviewed_at ? (
              <DateDisplay
                utcValue={reviewMeta[row.original.id]!.reviewed_at!}
                className="text-sm"
                optionOverrides={{ dateStyle: 'long', timeStyle: 'short', timeZoneName: undefined }}
                displayInUTC={false}
              />
            ) : <span className="text-sm">-</span>
          )
        },
        // dynamically render score columns derived from review metadata
        ...scoreTypes.map((type) => ({
          header: type,
          id: type.toLowerCase().replace(/\s+/g, '_'),
          cell: ({ row }: { row: { original: ConversationRow } }) => {
            const val = reviewMeta[row.original.id]?.scores?.[type];
            const isLow = typeof val === 'number' && val < 3;
            return (
              <div className={`text-sm ${isLow ? 'bg-yellow-100 text-yellow-900 -m-2 p-2 rounded' : ''}`}>{val ?? '-'}</div>
            );
          }
        })),
        {
          header: L.ai_coach.dashboard.url,
          id: "url",
          cell: ({ row }: { row: { original: ConversationRow } }) => (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => history.push(`/ai/config/review/${row.original.id}`)}
            >
              {L.ai_coach.dashboard.view_conversation}
            </Button>
          )
        },
        {
          header: L.ai_coach.dashboard.actions,
          id: "actions",
          cell: ({ row }: { row: { original: ConversationRow } }) => (
            <Button variant="primary" size="sm" onClick={() => history.push(`/ai/config/review/${row.original.id}`)}>
              {L.ai_coach.dashboard.view_edit}
            </Button>
          )
        }
      ]);
    }

    return cols;
  };

  const toReviewColumns = useMemo(() => buildColumns(history, 'toReview', scoreTypes), [history, reviewMeta, scoreTypes]);
  const reviewedColumns = useMemo(() => buildColumns(history, 'reviewed', scoreTypes), [history, reviewMeta, scoreTypes]);

  useAsyncEffect(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getConversations({
        limit: 100,
        offset: 0
      });

      if ('error' in response) {
        setConversations([]);
        setReviewedConversations([]);
        setError(typeof response.error === 'string' ? response.error : 'Failed to load conversations');
        return;
      }
      
      if ('results' in response) {
        const items: ConversationRow[] = response.results.map((conv) => {
          const withReviewed = conv as { reviewed_by_me?: boolean };
          const reviewed_by_me = withReviewed.reviewed_by_me === true;
          const withIdsAndDates = conv as { id: string | number, created_at: string | number | Date, updated_at: string | number | Date };
          const item: ConversationRow = {
            id: String(withIdsAndDates.id),
            created_at: new Date(withIdsAndDates.created_at as string | number),
            updated_at: new Date(withIdsAndDates.updated_at as string | number),
            created_by: conv.created_by,
            tokens_total: Number(conv.tokens_total),
            from_url: conv.from_url,
            legal_name: conv.legal_name,
            unsubmitted: conv.unsubmitted,
            duration: Number(conv.duration),
            feedback_positive: Number(conv.feedback_positive),
            feedback_negative: Number(conv.feedback_negative),
            transcript: (conv as any).transcript || null,
            feedback_reason: (conv as any).feedback_reason || null,
            total_count: conv.total_count,
            reviewed_by_me,
          };
          return item;
        });

        const toReview = items.filter(c => !c.reviewed_by_me);
        const reviewed = items.filter(c => !!c.reviewed_by_me);
        const sortByCreatedDesc = (a: ConversationRow, b: ConversationRow) => b.created_at.getTime() - a.created_at.getTime();
        const toReviewSorted = [...toReview].sort(sortByCreatedDesc);
        const reviewedSorted = [...reviewed].sort(sortByCreatedDesc);
        setConversations(toReviewSorted);
        setReviewedConversations(reviewedSorted);

        // Fetch score metadata for reviewed rows
        const reviewer = userInfo?.uid;
        if (reviewer && reviewedSorted.length) {
          const entries = await Promise.all(reviewedSorted.map(async (r) => {
            const resp = await getScores({ conversationId: r.id });
            if (Array.isArray(resp)) {
              const items: ScoreModel[] = resp as ScoreModel[];
              const myScores = items.filter((s) => s.created_by === reviewer);
              const getUpdatedMs = (s: ScoreModel): number => s.updatedAt ?? s.createdAt ?? 0;
              const latest = myScores.reduce<ScoreModel | undefined>((acc, s) => {
                const t = getUpdatedMs(s);
                if (!acc) return s;
                return getUpdatedMs(acc) < t ? s : acc;
              }, undefined);
              const scoreMap: Record<string, number> = {};
              for (const s of myScores) {
                scoreMap[s.score_type] = Number(s.value);
              }
              return [r.id, { reviewed_at: latest?.updatedAt || latest?.createdAt, scores: scoreMap }] as const;
            }
            return [r.id, { scores: {} }] as const;
          }));
          setReviewMeta(Object.fromEntries(entries));
        } else {
          setReviewMeta({});
        }
      }
    } catch (e: unknown) {
      setConversations([]);
      setReviewedConversations([]);
      setError(e instanceof Error ? e.message : 'Unknown error while loading conversations');
      setReviewMeta({});
    } finally {
      setLoading(false);
    }
  }, [userInfo?.uid]);

  if (loading) {
    return <Loading loading={true} />;
  }

  const reviewerId = new URLSearchParams(window.location.search).get('createdby') ?? userInfo?.uid ?? '';

  return (
    <ThreeColumnPage
      title={L.ai_coach.dashboard.conversations_review}
      main={
        <div className="py-8 px-4">
          <style>
            {`
            .no-page-size .pt-2.flex { gap: 0.5rem; }
            .no-page-size .pt-2.flex > div:first-child { display: none; }
            `}
          </style>
          <Heading level="h1">{L.ai_coach.dashboard.review_queue}</Heading>
          {error && (
            <p className="text-red-600 mt-2 mb-4">{error}</p>
          )}
          
          {conversations.length === 0 && reviewedConversations.length === 0 && !error ? (
            <p>{L.ai_coach.dashboard.no_conversations_review}</p>
          ) : (
            <>
              <Heading level="h2">{L.ai_coach.dashboard.to_review}</Heading>
              <div className="no-page-size">
                <DataTable
                  className="mb-10"
                  pageSize={5}
                  resizableColumns={true}
                  enableSearch={true}
                  columnPinningState={{ left: ['id'] }}
                  data={conversations}
                  columns={toReviewColumns}
                />
              </div>

              <Heading level="h2" className="mt-6">{L.ai_coach.dashboard.reviewed_by.replace('$reviewer', reviewerId)}</Heading>
              <div className="no-page-size">
                <DataTable
                  pageSize={5}
                  resizableColumns={true}
                  enableSearch={true}
                  columnPinningState={{ left: ['id'] }}
                  data={reviewedConversations}
                  columns={reviewedColumns}
                />
              </div>
            </>
          )}
        </div>
      }
    />
  );
}
